import { api } from './api';

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const detectFormat = (url: string, fallback: 'png' | 'jpg' = 'png'): 'png' | 'jpg' => {
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg') || lowerUrl.includes('format=jpg') || lowerUrl.includes('format=jpeg')) {
    return 'jpg';
  }

  return fallback;
};

export const downloadService = {
  async downloadFileFromUrl(
    url: string,
    filename: string,
    options?: {
      useProxy?: boolean;
      format?: 'png' | 'jpg';
      resolution?: 'web' | 'print';
    }
  ): Promise<void> {
    const format = options?.format || detectFormat(url);

    try {
      if (options?.useProxy) {
        const response = await api.get(`/api/download/external/${format}`, {
          params: {
            resolution: options?.resolution || 'web',
            sourceUrl: url,
          },
          responseType: 'blob',
        });

        const normalizedFilename = filename.replace(/\.(png|jpg|jpeg)$/i, `.${format}`);
        triggerBlobDownload(response.data, normalizedFilename);
        return;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }

      const blob = await response.blob();
      triggerBlobDownload(blob, filename);
    } catch (error) {
      console.error('Direct download failed:', error);
      throw new Error('Failed to download file. Please try again.');
    }
  },

  // Download a single mockup file
  async downloadMockup(
    mockupId: string, 
    filename: string, 
    format: 'png' | 'jpg' = 'png',
    resolution: 'web' | 'print' = 'web',
    sourceUrl?: string
  ): Promise<void> {
    try {
      const response = await api.get(`/api/download/${mockupId}/${format}`, {
        params: { resolution, sourceUrl },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: format === 'png' ? 'image/png' : 'image/jpeg'
      });
      triggerBlobDownload(blob, filename.replace(/\.(png|jpg|jpeg)$/i, `.${format}`));

      console.log(`Downloaded ${filename} as ${format}`);
    } catch (error) {
      console.error('Download failed:', error);
      throw new Error('Failed to download mockup. Please try again.');
    }
  },
  
  // Download multiple mockups as a ZIP file
  async downloadBatch(mockupIds: string[], format: 'png' | 'jpg' = 'png'): Promise<void> {
    try {
      const response = await api.post('/api/download/batch', 
        { mockupIds, format },
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      triggerBlobDownload(blob, `mockups-${new Date().getTime()}.zip`);
      
      console.log('Downloaded batch mockups as ZIP');
    } catch (error) {
      console.error('Batch download failed:', error);
      throw new Error('Failed to download mockups. Please try again.');
    }
  }
};
